import React, { useState, useEffect } from "react";
import { X, Rocket, ChevronRight, CheckCircle2, Sparkles } from "lucide-react";

const WELCOME_DISMISSED_KEY = "merchant_config_welcome_dismissed";

export function WelcomeOverlay({ onDismiss, onStartWizard, configuredCount, totalCount }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-[calc(100vw-1.5rem)] sm:max-w-lg bg-white rounded-xl sm:rounded-2xl shadow-2xl p-5 sm:p-6 md:p-8 animate-in zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto">
        <button
          type="button"
          onClick={onDismiss}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors touch-manipulation"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="text-center mb-5 sm:mb-6">
          <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 rounded-xl sm:rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
            <Rocket className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
          </div>
          <h2 className="text-lg sm:text-xl font-bold text-slate-900">Welcome to Account Configuration</h2>
          <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-slate-500 max-w-sm mx-auto">
            Set up your account by configuring each module. Each card represents a configuration category - click to configure.
          </p>
        </div>

        <div className="space-y-2.5 sm:space-y-3 mb-5 sm:mb-6">
          <div className="flex items-start gap-2.5 sm:gap-3 p-2.5 sm:p-3 rounded-lg sm:rounded-xl bg-slate-50 border border-slate-100">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
              <span className="text-xs sm:text-sm font-bold text-blue-600">1</span>
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-slate-700">Browse configuration cards</p>
              <p className="text-[11px] sm:text-xs text-slate-500">Each card shows its status - configured or pending</p>
            </div>
          </div>
          <div className="flex items-start gap-2.5 sm:gap-3 p-2.5 sm:p-3 rounded-lg sm:rounded-xl bg-slate-50 border border-slate-100">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
              <span className="text-xs sm:text-sm font-bold text-blue-600">2</span>
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-slate-700">Click any card to configure</p>
              <p className="text-[11px] sm:text-xs text-slate-500">Follow the built-in guide for step-by-step help</p>
            </div>
          </div>
          <div className="flex items-start gap-2.5 sm:gap-3 p-2.5 sm:p-3 rounded-lg sm:rounded-xl bg-slate-50 border border-slate-100">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
              <span className="text-xs sm:text-sm font-bold text-blue-600">3</span>
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-slate-700">Track your progress</p>
              <p className="text-[11px] sm:text-xs text-slate-500">The progress bar shows overall completion at a glance</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3">
          {configuredCount < totalCount && (
            <button
              type="button"
              onClick={onStartWizard}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 sm:py-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors shadow-sm touch-manipulation min-h-[44px]"
            >
              <Sparkles className="w-4 h-4" />
              Start Setup Wizard
            </button>
          )}
          <button
            type="button"
            onClick={onDismiss}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 sm:py-3 border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium rounded-xl transition-colors touch-manipulation min-h-[44px]"
          >
            I'll explore on my own
          </button>
        </div>
      </div>
    </div>
  );
}

export function SetupWizardBanner({ unconfiguredCategories, onGoToCategory, onDismiss }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!unconfiguredCategories || unconfiguredCategories.length === 0) return null;

  const current = unconfiguredCategories[currentIndex % unconfiguredCategories.length];
  const Icon = current.icon;

  const goNext = () => {
    if (currentIndex < unconfiguredCategories.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  return (
    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl sm:rounded-2xl p-3.5 sm:p-4 md:p-5 text-white shadow-lg shadow-blue-500/10">
      <div className="flex items-center justify-between gap-2 sm:gap-3 mb-3">
        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
          <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-200" />
          <span className="text-xs sm:text-sm font-semibold text-blue-100">Setup Wizard</span>
          <span className="text-[10px] sm:text-xs text-blue-300">
            Step {currentIndex + 1} of {unconfiguredCategories.length}
          </span>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="p-1.5 text-blue-200 hover:text-white hover:bg-white/10 rounded-lg transition-colors touch-manipulation"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex items-center gap-3 sm:gap-4">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-white/10 border border-white/20 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-xs sm:text-sm font-semibold text-white truncate">{current.label}</h4>
          <p className="text-[11px] sm:text-xs text-blue-200 mt-0.5 line-clamp-2">{current.description}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-3 sm:mt-4">
        <button
          type="button"
          onClick={() => onGoToCategory(current)}
          className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-white text-blue-700 text-xs sm:text-sm font-medium rounded-lg hover:bg-blue-50 transition-colors touch-manipulation min-h-[36px] sm:min-h-0"
        >
          Configure Now
          <ChevronRight className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
        </button>
        {currentIndex < unconfiguredCategories.length - 1 && (
          <button
            type="button"
            onClick={goNext}
            className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-white/10 border border-white/20 text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-white/20 transition-colors touch-manipulation min-h-[36px] sm:min-h-0"
          >
            Skip
          </button>
        )}
      </div>

      {/* Progress dots */}
      <div className="flex items-center gap-1 mt-3 overflow-x-auto scrollbar-none">
        {unconfiguredCategories.map((_, i) => (
          <div
            key={i}
            className={`h-1 rounded-full transition-all shrink-0 ${
              i === currentIndex ? "w-4 bg-white" : i < currentIndex ? "w-2 bg-white/60" : "w-2 bg-white/30"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export function useWelcomeGuide() {
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(WELCOME_DISMISSED_KEY);
    if (!dismissed) {
      setShowWelcome(true);
    }
  }, []);

  const dismissWelcome = () => {
    setShowWelcome(false);
    localStorage.setItem(WELCOME_DISMISSED_KEY, "true");
  };

  return { showWelcome, dismissWelcome };
}
